import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { Link } from 'react-router-dom'
import { Plus, Edit, Trash2, Search, Building2 } from 'lucide-react'
import toast from 'react-hot-toast'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { Input } from '../components/ui/Input'
import { Select } from '../components/ui/Select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/Table'
import { Badge } from '../components/ui/Badge'
import { modelsApi, brandsApi } from '../lib/api'
import { formatNumber } from '../lib/utils'
import { useLanguage } from '../contexts/LanguageContext'

export default function Models() {
  const [filters, setFilters] = useState({
    search: '',
    manufacturer_id: '',
    page: 1,
    limit: 10
  })
  const [deleteId, setDeleteId] = useState(null)
  const queryClient = useQueryClient()

  const { data, isLoading, error } = useQuery(
    ['models', filters],
    () => modelsApi.getAll(filters),
    {
      select: (data) => data.data,
      keepPreviousData: true,
      retry: 1,
      refetchOnWindowFocus: false
    }
  )

  const { data: brandsData } = useQuery(
    'brands-for-models',
    () => brandsApi.getAll({ limit: 100 }),
    {
      select: (data) => data.data?.results || []
    }
  )

  const deleteMutation = useMutation(
    (id) => modelsApi.delete(id),
    {
      onSuccess: () => {
        toast.success('Model deleted successfully')
        queryClient.invalidateQueries(['models'])
        setDeleteId(null)
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to delete model')
      }
    }
  )

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: 1
    }))
  }

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this model?')) {
      deleteMutation.mutate(id)
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Models</h1>
          <p className="mt-1 text-sm text-gray-600">
            Manage car models and their relationships with brands
          </p>
        </div>
        <Link to="/models/new">
          <Button>
            <Plus className="h-4 w-4 mr-2" />
            Add Model
          </Button>
        </Link>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Search className="h-5 w-5 mr-2" />
            Search Models
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="label">Search</label>
              <Input
                placeholder="Search models..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
              />
            </div>
            <div>
              <label className="label">Brand</label>
              <Select
                value={filters.manufacturer_id}
                onChange={(e) => handleFilterChange('manufacturer_id', e.target.value)}
              >
                <option value="">All Brands</option>
                {brandsData?.map((brand) => (
                  <option key={brand.id} value={brand.id}>
                    {brand.name}
                  </option>
                ))}
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>
            Models ({data?.count || 0})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {error ? (
            <div className="text-center py-12">
              <p className="text-red-600">Error loading models: {error.message}</p>
            </div>
          ) : isLoading ? (
            <div className="space-y-4">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="animate-pulse">
                  <div className="h-4 bg-gray-200 rounded w-full mb-2" />
                  <div className="h-4 bg-gray-200 rounded w-3/4" />
                </div>
              ))}
            </div>
          ) : data?.results?.length > 0 ? (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Model Name</TableHead>
                    <TableHead>Brand</TableHead>
                    <TableHead>Slug</TableHead>
                    <TableHead>Vehicles</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.results.map((model) => (
                    <TableRow key={model.id}>
                      <TableCell className="font-medium">{model.name}</TableCell>
                      <TableCell>
                        <div className="flex items-center">
                          <Building2 className="h-4 w-4 mr-2 text-gray-400" />
                          {model.manufacturer_name}
                        </div>
                      </TableCell>
                      <TableCell>{model.slug}</TableCell>
                      <TableCell>{formatNumber(model.vehicle_count)}</TableCell>
                      <TableCell>
                        <Badge variant={model.is_active ? 'success' : 'gray'}>
                          {model.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center space-x-2">
                          <Link to={`/models/${model.id}/edit`}>
                            <Button variant="ghost" size="sm">
                              <Edit className="h-4 w-4" />
                            </Button>
                          </Link>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-600 hover:text-red-700"
                            onClick={() => handleDelete(model.id)}
                            disabled={deleteMutation.isLoading && deleteId === model.id}
                          >
                            {deleteMutation.isLoading && deleteId === model.id ? (
                              <div className="loading-spinner" />
                            ) : (
                              <Trash2 className="h-4 w-4" />
                            )}
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <div className='flex justify-center gap-2 flex-wrap mt-4'>
                {
                  Array(data.pages).fill('').map((_, i) => <Button onClick={() => setFilters(p => ({ ...p, page: (i + 1) }))}> {i + 1} </Button>)
                }

              </div>
            </>
          ) : (
            <div className="text-center py-12">
              <Building2 className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No models</h3>
              <p className="mt-1 text-sm text-gray-500">
                Get started by adding your first model.
              </p>
              <div className="mt-6">
                <Link to="/models/new">
                  <Button>
                    <Plus className="h-4 w-4 mr-2" />
                    Add Model
                  </Button>
                </Link>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
